import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_URL;

const loginUser = async ({ email, password }) => {
  const response = await fetch(`${API_BASE_URL}/api/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password })
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(
      data.error || data.messages?.email?.[0] || data.messages?.password?.[0] || 'Login failed'
    );
  }

  return data; 
};

// Function to verify token with backend
const verifyToken = async (token) => {
  if (!token) return false;
  
  try {
    const response = await fetch(`${API_BASE_URL}/api/me`, {
      method: 'GET',
      headers: { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    
    return response.ok;
  } catch (error) {
    return false;
  }
};

export const useAuth = () => {
  const queryClient = useQueryClient();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Check if user is authenticated on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      
      if (token) {
        const isValid = await verifyToken(token);
        setIsLoggedIn(isValid);
        
        if (isValid) {
          // Get user data
          try {
            const response = await fetch(`${API_BASE_URL}/api/me`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
            }
          } catch (error) {
            console.error('Failed to get user data:', error);
          }
        } else {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, []);

  const loginMutation = useMutation({
    mutationFn: loginUser,
    onSuccess: (data) => {
      // Store token and user data in localStorage
      localStorage.setItem('authToken', data.access_token);
      localStorage.setItem('user', JSON.stringify(data.user));
      
      setUser(data.user);
      setIsLoggedIn(true);
      queryClient.clear();
      navigate('/Dashboard');
    },
    onError: (error) => {
      console.error('Login failed:', error);
      localStorage.removeItem('authToken');
      localStorage.removeItem('user');
      setUser(null);
      setIsLoggedIn(false);
    }
  });

  const logout = async () => {
    const token = localStorage.getItem('authToken');
    
    try {
      if (token) {
        await fetch(`${API_BASE_URL}/api/logout`, {
          method: 'POST',
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json' 
          }
        });
      }
    } catch (error) {
      console.log('Logout API call failed, continuing...');
    }

    // Clear localStorage and state
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setUser(null);
    setIsLoggedIn(false);
    queryClient.clear();
    navigate('/auth');
  };

  // Helper function to get current token
  const getToken = () => localStorage.getItem('authToken');

  return {
    isLoggedIn,
    isLoading,
    user,
    login: loginMutation.mutate,
    logout,
    getToken,
    loginError: loginMutation.error,
    loginLoading: loginMutation.isPending
  };
};